/**
 * RichDraftEditor — Cluster H.2 (Feb 2026), v3.
 *
 * Tiptap-based rich editor. v3 uses Tiptap's ReactNodeViewRenderer to
 * render per-paragraph React components, which is the ProseMirror-native
 * way to attach UI affordances to a node. v2's MutationObserver +
 * direct DOM injection caused ProseMirror to fight back on every
 * transaction (re-rendering the paragraph, removing the foreign chip,
 * which re-triggered the observer — a perf death spiral that hung the
 * page).
 *
 * Each paragraph/heading mounts a `<ParagraphView>` (NodeViewWrapper +
 * NodeViewContent). The gutter chip is a React-controlled `<span>`
 * sibling to the content — ProseMirror leaves NodeView-rendered DOM
 * alone because it knows about it through the NodeView contract.
 *
 * Markdown → HTML happens once on mount (decision 3 adj).
 *
 * Exposed testids:
 *   rich-draft-editor-root            -- outer wrapper
 *   rich-draft-editor                 -- the ProseMirror contenteditable
 *   paragraph-gutter-<idx>            -- per-paragraph gutter
 *   paragraph-edit-<idx>              -- edit action
 *   paragraph-highlight-<idx>         -- highlight action
 *   paragraph-comment-<idx>           -- comment action (opens annotation)
 *   paragraph-delete-<idx>            -- delete action
 */
import React, { useMemo } from 'react';
import {
  EditorContent,
  useEditor,
  NodeViewWrapper,
  NodeViewContent,
  ReactNodeViewRenderer,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Paragraph from '@tiptap/extension-paragraph';
import Heading from '@tiptap/extension-heading';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';

const ACCENT = '#d6b27d';

function markdownToHtml(md) {
  if (!md) return '<p></p>';
  const lines = String(md).split(/\r?\n/);
  const out = [];
  let buf = [];
  const inline = (s) => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>');
  const flush = () => {
    if (!buf.length) return;
    const text = buf.join(' ').trim();
    if (text) out.push(`<p>${inline(text)}</p>`);
    buf = [];
  };
  for (const ln of lines) {
    if (!ln.trim()) { flush(); continue; }
    const h = ln.match(/^(#{1,3})\s+(.+)$/);
    if (h) {
      flush();
      const lvl = h[1].length;
      out.push(`<h${lvl}>${inline(h[2])}</h${lvl}>`);
    } else {
      buf.push(ln);
    }
  }
  flush();
  return out.join('') || '<p></p>';
}

/**
 * Compute the 0-based index of this top-level node by walking the doc
 * once. getPos() returns the absolute pos; we match it against the
 * offset of each top-level child.
 */
function computeIndex(editor, getPos) {
  try {
    if (!editor?.state?.doc || typeof getPos !== 'function') return 0;
    const pos = getPos();
    if (typeof pos !== 'number') return 0;
    let found = 0;
    editor.state.doc.forEach((_, offset, index) => {
      if (offset === pos) found = index;
    });
    return found;
  } catch {
    return 0;
  }
}

/** Per-paragraph React NodeView. */
const ParagraphView = ({ editor, getPos, node, isArabic, onCommentClause, tag = 'p' }) => {
  const idx = computeIndex(editor, getPos);

  const runHighlight = (e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const from = getPos() + 1;
      const to = from + node.nodeSize - 2;
      editor.chain().focus().setTextSelection({ from, to }).toggleHighlight().run();
    } catch { /* noop */ }
  };
  const runDelete = (e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const from = getPos();
      const to = from + node.nodeSize;
      editor.chain().focus().deleteRange({ from, to }).run();
    } catch { /* noop */ }
  };
  const runEdit = (e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const to = getPos() + node.nodeSize - 1;
      editor.chain().focus().setTextSelection(to).run();
    } catch { /* noop */ }
  };
  const runComment = (e) => {
    e.preventDefault(); e.stopPropagation();
    onCommentClause?.({ paragraphIdx: idx, clauseId: `clause_${idx + 1}` });
  };

  return (
    <NodeViewWrapper
      as={tag}
      className="rd-block"
      data-clause-idx={idx}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <NodeViewContent as="span" className="rd-content" />
      <span
        className="rd-gutter"
        contentEditable={false}
        data-testid={`paragraph-gutter-${idx}`}
      >
        <button type="button" onMouseDown={runEdit} data-testid={`paragraph-edit-${idx}`} title={isArabic ? 'تحرير' : 'Edit'} aria-label="edit">✎</button>
        <button type="button" onMouseDown={runHighlight} data-testid={`paragraph-highlight-${idx}`} title={isArabic ? 'تمييز' : 'Highlight'} aria-label="highlight">▮</button>
        <button type="button" onMouseDown={runComment} data-testid={`paragraph-comment-${idx}`} title={isArabic ? 'تعليق' : 'Comment'} aria-label="comment">💬</button>
        <button type="button" onMouseDown={runDelete} data-testid={`paragraph-delete-${idx}`} title={isArabic ? 'حذف' : 'Delete'} aria-label="delete">✕</button>
      </span>
    </NodeViewWrapper>
  );
};

export const RichDraftEditor = ({
  content, onChange, isArabic, onCommentClause,
}) => {
  const initialHtml = useMemo(() => markdownToHtml(content), [content]);

  const ParagraphExt = useMemo(() => Paragraph.extend({
    addNodeView() {
      return ReactNodeViewRenderer((props) => (
        <ParagraphView {...props} isArabic={isArabic} onCommentClause={onCommentClause} tag="p" />
      ));
    },
  }), [isArabic, onCommentClause]);

  const HeadingExt = useMemo(() => Heading.configure({ levels: [1, 2, 3] }).extend({
    addNodeView() {
      return ReactNodeViewRenderer((props) => {
        const lvl = props.node?.attrs?.level || 1;
        return (
          <ParagraphView {...props} isArabic={isArabic} onCommentClause={onCommentClause} tag={`h${lvl}`} />
        );
      });
    },
  }), [isArabic, onCommentClause]);

  const editor = useEditor({
    extensions: [
      // Tiptap 3.x: configure StarterKit to skip paragraph + heading so
      // our custom NodeView-aware versions can replace them cleanly.
      StarterKit.configure({ paragraph: false, heading: false }),
      ParagraphExt,
      HeadingExt,
      Highlight.configure({ multicolor: false }),
      Placeholder.configure({
        placeholder: isArabic ? 'ابدأ الكتابة…' : 'Start writing…',
      }),
    ],
    content: initialHtml,
    editorProps: {
      attributes: {
        class: 'rich-draft-prose',
        dir: isArabic ? 'rtl' : 'ltr',
        'data-testid': 'rich-draft-editor',
      },
    },
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
  });

  if (!editor) return null;

  return (
    <div data-testid="rich-draft-editor-root" className="relative">
      <EditorContent editor={editor} />
      <style>{`
        .rich-draft-prose { outline: none; min-height: 260px; }
        .rich-draft-prose .rd-block {
          position: relative;
          margin: 0 0 14px;
          line-height: 1.7;
          padding-${isArabic ? 'left' : 'right'}: 116px;
          min-height: 1.5em;
        }
        .rich-draft-prose h1.rd-block,
        .rich-draft-prose h2.rd-block,
        .rich-draft-prose h3.rd-block { font-weight: 600; line-height: 1.4; margin: 1.2em 0 0.6em; }
        .rich-draft-prose h1.rd-block { font-size: 1.7em; }
        .rich-draft-prose h2.rd-block { font-size: 1.35em; }
        .rich-draft-prose h3.rd-block { font-size: 1.15em; }
        .rich-draft-prose mark { background: rgba(214, 178, 125, 0.35); color: inherit; padding: 0 2px; border-radius: 2px; }
        .rich-draft-prose code { background: rgba(255,255,255,0.06); padding: 0 4px; border-radius: 3px; font-size: 0.92em; }
        .rich-draft-prose .is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: rgba(255,255,255,0.3);
          float: left;
          pointer-events: none;
          height: 0;
        }
        .rich-draft-prose .rd-gutter {
          position: absolute;
          top: 2px;
          ${isArabic ? 'left' : 'right'}: 0;
          display: inline-flex;
          gap: 4px;
          opacity: 0;
          transition: opacity 120ms;
          pointer-events: none;
          user-select: none;
        }
        .rich-draft-prose .rd-block:hover .rd-gutter,
        .rich-draft-prose .rd-gutter:hover {
          opacity: 1;
          pointer-events: auto;
        }
        .rich-draft-prose .rd-gutter button {
          background: rgba(20,20,20,0.92);
          border: 1px solid ${ACCENT}55;
          color: ${ACCENT};
          width: 22px; height: 22px;
          border-radius: 4px;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer;
          font-size: 12px;
          line-height: 1;
          padding: 0;
        }
        .rich-draft-prose .rd-gutter button:hover {
          background: rgba(40,40,40,1);
        }
      `}</style>
    </div>
  );
};

export default RichDraftEditor;
